import { useEffect, useRef } from 'react';

// Pointer-based drag and drop that works with mouse and touch (HTML5 drag events don't
// fire on phones). Mouse drags start after a few pixels; touch drags start on long-press.
//
//   const drag = useDraggable({ type: 'task', id: task.id, label: task.title });
//   <div {...drag}>…</div>
//
//   const ref = useDropTarget<HTMLDivElement>(['task'], (data, at) => …, { date: '2026-10-25' });
//   <div ref={ref}>…</div>

export interface DragData {
  type: string;
  id: string;
  /** text shown in the floating ghost */
  label?: string;
  /** over drop targets matching this selector the ghost hides (they draw their own preview) */
  ghostHiddenOver?: string;
  [k: string]: unknown;
}

export interface DropInfo {
  x: number;
  y: number;
  /** the drop target element */
  el: HTMLElement;
  /** the target's own payload passed to useDropTarget */
  target: Record<string, unknown>;
}

interface Target {
  accept: string[];
  onDrop: (data: DragData, info: DropInfo) => void;
  onOver?: (data: DragData, info: DropInfo) => void;
  payload: Record<string, unknown>;
}

const targets = new WeakMap<HTMLElement, Target>();
let active: { data: DragData; ghost: HTMLElement; over: HTMLElement | null } | null = null;
const dragListeners = new Set<(d: DragData | null) => void>();

/** Subscribe to drag start/end (e.g. to show drop zones only while dragging). */
export function onDragChange(fn: (d: DragData | null) => void): () => void {
  dragListeners.add(fn);
  return () => dragListeners.delete(fn);
}

export const isDragging = () => active !== null;

function findTarget(x: number, y: number, type: string): HTMLElement | null {
  let el = document.elementFromPoint(x, y) as HTMLElement | null;
  while (el) {
    const t = targets.get(el);
    if (t?.accept.includes(type)) return el;
    el = el.parentElement;
  }
  return null;
}

function autoScroll(x: number, y: number) {
  let el = document.elementFromPoint(x, y) as HTMLElement | null;
  while (el && el !== document.body) {
    const s = getComputedStyle(el);
    if (/(auto|scroll)/.test(s.overflowY + s.overflowX)) {
      const r = el.getBoundingClientRect();
      const edge = 48;
      if (y < r.top + edge) el.scrollTop -= 12;
      else if (y > r.bottom - edge) el.scrollTop += 12;
      if (x < r.left + edge) el.scrollLeft -= 12;
      else if (x > r.right - edge) el.scrollLeft += 12;
      return;
    }
    el = el.parentElement;
  }
}

// The ghost moves with a transform (no layout), and the work per move (finding the target,
// hover previews, auto-scroll) runs at most once per frame: mice can report 1000 moves a second.
const placeGhost = (ghost: HTMLElement, x: number, y: number) => {
  ghost.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -120%) rotate(-1.5deg)`;
};
let frame = 0;
let pendingMove: { x: number; y: number } | null = null;

function scheduleMove(x: number, y: number) {
  pendingMove = { x, y };
  // Hidden pages get no animation frames.
  if (document.hidden) return flushMove();
  if (!frame) frame = requestAnimationFrame(flushMove);
}

function flushMove() {
  if (frame) cancelAnimationFrame(frame);
  frame = 0;
  const p = pendingMove;
  pendingMove = null;
  if (p) moveDrag(p.x, p.y);
}

function startDrag(data: DragData, x: number, y: number) {
  // A drag that never got its pointer-up (window lost focus mid-drag) mustn't leave its
  // ghost on screen.
  if (active) endDrag(0, 0, true);
  for (const g of document.querySelectorAll('.e-drag-ghost')) g.remove();
  const ghost = document.createElement('div');
  ghost.className = 'e-drag-ghost';
  ghost.textContent = data.label ?? 'Moving';
  placeGhost(ghost, x, y);
  document.body.appendChild(ghost);
  document.body.classList.add('e-dragging');
  active = { data, ghost, over: null };
  for (const l of dragListeners) l(data);
}

function moveDrag(x: number, y: number) {
  if (!active) return;
  placeGhost(active.ghost, x, y);
  const over = findTarget(x, y, active.data.type);
  if (over !== active.over) {
    active.over?.removeAttribute('data-drop-over');
    over?.setAttribute('data-drop-over', 'true');
    active.over = over;
    const sel = active.data.ghostHiddenOver;
    active.ghost.style.visibility = sel && over?.matches(sel) ? 'hidden' : '';
  }
  if (over) {
    const t = targets.get(over)!;
    t.onOver?.(active.data, { x, y, el: over, target: t.payload });
  }
  autoScroll(x, y);
}

function endDrag(x: number, y: number, cancel = false) {
  // Catch up on a move still waiting for its frame, so the drop lands where it looks.
  if (!cancel) flushMove();
  else {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    pendingMove = null;
  }
  if (!active) return;
  const { data, ghost, over } = active;
  ghost.remove();
  document.body.classList.remove('e-dragging');
  over?.removeAttribute('data-drop-over');
  active = null;
  for (const l of dragListeners) l(null);
  if (cancel || !over) return;
  const t = targets.get(over);
  t?.onDrop(data, { x, y, el: over, target: t.payload });
}

/** Spread the returned props on the element you want to drag. */
export function useDraggable(
  data: DragData,
  opts: { disabled?: boolean; longPressMs?: number } = {},
) {
  const dataRef = useRef(data);
  dataRef.current = data;
  const { disabled, longPressMs = 280 } = opts;

  const onPointerDown = (e: React.PointerEvent) => {
    // A new press while an old drag still shows: that one is over.
    if (active) endDrag(0, 0, true);
    if (disabled || e.button !== 0) return;
    const target = e.target as HTMLElement;
    // Don't hijack inputs/buttons inside the draggable.
    if (target.closest('input, textarea, [contenteditable="true"], [data-no-drag]')) return;
    const sx = e.clientX;
    const sy = e.clientY;
    const touch = e.pointerType !== 'mouse';
    let started = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastX = sx;
    let lastY = sy;

    const begin = () => {
      started = true;
      navigator.vibrate?.(12);
      startDrag(dataRef.current, lastX, lastY);
    };
    if (touch) timer = setTimeout(begin, longPressMs);

    const move = (ev: PointerEvent) => {
      lastX = ev.clientX;
      lastY = ev.clientY;
      if (!started) {
        const dist = Math.hypot(ev.clientX - sx, ev.clientY - sy);
        if (touch) {
          // Finger moved before the long-press: it's a scroll, not a drag.
          if (dist > 8 && timer) {
            clearTimeout(timer);
            timer = null;
            cleanup();
          }
          return;
        }
        if (dist < 6) return;
        begin();
      }
      ev.preventDefault();
      scheduleMove(ev.clientX, ev.clientY);
    };
    const touchBlock = (ev: TouchEvent) => {
      if (started) ev.preventDefault();
    };
    const up = (ev: PointerEvent) => {
      cleanup();
      if (started) endDrag(ev.clientX, ev.clientY);
    };
    const cancel = () => {
      cleanup();
      if (started) endDrag(0, 0, true);
    };
    const key = (ev: KeyboardEvent) => ev.key === 'Escape' && cancel();
    function cleanup() {
      if (timer) clearTimeout(timer);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('touchmove', touchBlock);
      window.removeEventListener('keydown', key);
      window.removeEventListener('blur', cancel);
    }
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('touchmove', touchBlock, { passive: false });
    window.addEventListener('keydown', key);
    // Released outside the window (the up never arrives): stop rather than stick.
    window.addEventListener('blur', cancel);
  };

  return {
    onPointerDown,
    // Stop the browser's own image/text dragging from interfering.
    onDragStart: (e: React.DragEvent) => e.preventDefault(),
    style: { touchAction: 'pan-y' } as React.CSSProperties,
  };
}

/** Register an element as a drop target for the given drag types. */
export function useDropTarget<T extends HTMLElement>(
  accept: string[],
  onDrop: Target['onDrop'],
  payload: Record<string, unknown> = {},
  onOver?: Target['onOver'],
) {
  const ref = useRef<T | null>(null);
  const cfg = useRef<Target>({ accept, onDrop, payload, onOver });
  cfg.current.accept = accept;
  cfg.current.onDrop = onDrop;
  cfg.current.payload = payload;
  cfg.current.onOver = onOver;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const proxy: Target = {
      get accept() {
        return cfg.current.accept;
      },
      onDrop: (d, i) => cfg.current.onDrop(d, i),
      onOver: (d, i) => cfg.current.onOver?.(d, i),
      get payload() {
        return cfg.current.payload;
      },
    };
    targets.set(el, proxy);
    return () => {
      targets.delete(el);
    };
  }, []);
  return ref;
}
